import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
    TrackEvent,
} from '@enums';
import type { AnalyticsEvent } from '@hello-pimster/pimster-ui';
import type { TrackFn } from '@lib/analytics/types';
import { InternalMessageType } from '@lib/internal-messages';

export const mapPimsterUIEvent = (
    event: AnalyticsEvent,
    track: TrackFn
): void => {
    switch (event.name) {
        case 'story_page:view': {
            const {
                story,
                storyPage,
                index,
                videoDuration,
                viewDuration,
                loopCount,
            } = event.payload;
            track(TrackEvent.StoryPageView, {
                story_title: story.title ?? '',
                index,
                page_title: storyPage.title ?? undefined,
                video_duration: videoDuration,
                view_duration: viewDuration,
                loop_count: loopCount,
                total_pages: story.pageCount,
            });
            break;
        }

        case 'shoppable_detail:open': {
            track(TrackEvent.AdditionalProductDetailOpen, {
                additional_product_id: event.payload.shoppable.id,
            });
            break;
        }

        case 'shoppable:add_to_cart': {
            const { shoppable, variant } = event.payload;
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: variant?.url ?? shoppable.url ?? '',
                destination_name: variant?.name ?? shoppable.name,
                destination_category:
                    OutboundLinkDestinationCategory.AdditionalProduct,
                shape: OutboundLinkShape.Button,
                context: OutboundLinkContext.StoryPage,
                action: InternalMessageType.AddToCart,
                ...(variant && {
                    actionPayload: {
                        product: { id: shoppable.id, source: shoppable.source },
                        variant: {
                            id: variant.id,
                            gtin: variant.gtin ?? undefined,
                            source: variant.source,
                        },
                    },
                }),
            });
            break;
        }

        case 'shoppable:see': {
            const { shoppable, variant, shape } = event.payload;
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: variant?.url ?? shoppable.url ?? '',
                destination_name: variant?.name ?? shoppable.name,
                destination_category:
                    OutboundLinkDestinationCategory.AdditionalProduct,
                shape:
                    shape === 'icon-button'
                        ? OutboundLinkShape.IconButton
                        : OutboundLinkShape.Button,
                context: OutboundLinkContext.StoryPage,
            });
            break;
        }

        case 'story_page:link_click': {
            const { link } = event.payload;
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: link.href,
                destination_name: link.label ?? '',
                destination_category: OutboundLinkDestinationCategory.Unknown,
                shape: OutboundLinkShape.Button,
                context: OutboundLinkContext.StoryPage,
            });
            break;
        }

        case 'cart:view': {
            track(TrackEvent.OutboundLink, {
                category: 'click',
                destination: event.payload.cartUrl,
                destination_name: 'Cart',
                destination_category: OutboundLinkDestinationCategory.Cart,
                shape: OutboundLinkShape.InlineText,
            });
            break;
        }

        case 'story:view':
            // Handled by story navigation, which triggers a new page view
            break;
    }
};
